
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    if (isHomePage) {
      const section = document.getElementById(sectionId);
      if (section) {
        const navbarHeight = 80;
        const sectionTop =
          section.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
        window.scrollTo({ top: sectionTop, behavior: 'smooth' });
      }
    } else {
      navigate(`/#${sectionId}`);
    }
  };

  const goTo = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gradient-to-b from-[#010F0F] to-[#000000] text-[#E0EEEE] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 right-0 w-[50%] h-[60%] opacity-25"
          style={{
            background: 'radial-gradient(ellipse 80% 70% at 80% 20%, rgba(0,229,153,0.08) 0%, rgba(0,163,114,0.04) 40%, transparent 70%)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 w-[40%] h-[40%] opacity-15"
          style={{
            background: 'radial-gradient(ellipse 70% 60% at 20% 80%, rgba(0,184,128,0.06) 0%, transparent 60%)',
          }}
        />
        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.012]" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.5\'/%3E%3C/svg%3E")',
        }} />
      </div>

      {/* Footer content */}
      <div className="max-w-6xl mx-auto px-6 pt-16 md:pt-20 pb-8 relative z-10">
        <div className="grid md:grid-cols-5 gap-8 mb-10">
          {/* Logo & description */}
          <div className="md:col-span-1">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                handleLogoClick();
              }}
              className="cursor-pointer inline-block mb-4"
            >
              <img
                src="/Untitled design-3.png"
                alt="Revtain Logo"
                className="h-16 sm:h-20 w-auto transition-all duration-300 hover:opacity-80"
              />
            </a>
            <p className="text-xs text-[#E0EEEE]/40 leading-relaxed">
              Revenue recovery infrastructure that eliminates involuntary churn and
              secures your bottom line.
            </p>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-4">
              Product
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Product', id: 'section-product' },
                { label: 'Pricing', id: 'section-performance' },
                { label: 'FAQ', id: 'section-faq' },
              ].map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => scrollToSection(e, item.id)}
                    className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/integrations"
                  onClick={(e) => goTo(e, '/integrations')}
                  className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Integrations
                </a>
              </li>
              <li>
                <a
                  href="https://docs.revtain.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  API Docs
                </a>
              </li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-4">
              Solutions
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'E-Commerce', path: '/industry/e-commerce' },
                { label: 'Subscriptions', path: '/industry/subscriptions' },
              ].map((item) => (
                <li key={item.path}>
                  <a
                    href={item.path}
                    onClick={(e) => goTo(e, item.path)}
                    className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-4">
              Company
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="/about"
                  onClick={(e) => goTo(e, '/about')}
                  className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  onClick={(e) => goTo(e, '/blog')}
                  className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Blog
                </a>
              </li>
              <li>
                <a
                  href="#section-contact"
                  onClick={(e) => scrollToSection(e, 'section-contact')}
                  className="text-sm text-[#E0EEEE]/50 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-4">
              Connect
            </h4>
            <div className="flex gap-3 mb-6">
              <a
                href="#"
                className="w-9 h-9 flex items-center justify-center bg-[#E0EEEE]/5 border border-[#E0EEEE]/10 rounded-lg hover:bg-[#00E599] hover:border-[#00E599] transition-all duration-300 group cursor-pointer social-hover"
                rel="nofollow"
                onClick={(e) => e.preventDefault()}
              >
                <svg className="w-3.5 h-3.5 fill-[#E0EEEE]/50 group-hover:fill-[#001A1A] transition-colors duration-300" viewBox="0 0 1200 1227" xmlns="http://www.w3.org/2000/svg">
                  <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.163 519.284ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.828Z"/>
                </svg>
              </a>
              <a
                href="https://linkedin.com/company/revtain/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-[#E0EEEE]/5 border border-[#E0EEEE]/10 rounded-lg hover:bg-[#00E599] hover:border-[#00E599] transition-all duration-300 group cursor-pointer social-hover"
              >
                <i className="ph-fill ph-linkedin-logo text-sm text-[#E0EEEE]/50 group-hover:text-[#001A1A]"></i>
              </a>
            </div>

            {/* Legal links */}
            <h4 className="text-xs font-bold text-[#E0EEEE]/30 uppercase tracking-wider mb-3">
              Legal
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="/privacy-policy"
                  onClick={(e) => goTo(e, '/privacy-policy')}
                  className="text-xs text-[#E0EEEE]/40 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="/terms-of-service"
                  onClick={(e) => goTo(e, '/terms-of-service')}
                  className="text-xs text-[#E0EEEE]/40 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href="/cookie-policy"
                  onClick={(e) => goTo(e, '/cookie-policy')}
                  className="text-xs text-[#E0EEEE]/40 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Cookie Policy
                </a>
              </li>
              <li>
                <a
                  href="/acceptable-use-policy"
                  onClick={(e) => goTo(e, '/acceptable-use-policy')}
                  className="text-xs text-[#E0EEEE]/40 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Acceptable Use
                </a>
              </li>
              <li>
                <a
                  href="/security"
                  onClick={(e) => goTo(e, '/security')}
                  className="text-xs text-[#E0EEEE]/40 hover:text-[#00E599] transition-colors cursor-pointer"
                >
                  Security
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-6 border-t border-[#E0EEEE]/5 flex flex-col sm:flex-row justify-center items-center gap-3">
          <p className="text-xs text-[#E0EEEE]/30">
            &copy; {new Date().getFullYear()} Revtain. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
